import { toTwelveHour } from '@pams/shared';
/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * THE 24-HOUR COVERAGE OF A SHIFT PATTERN. Pure — no React, no luxon, no tokens.
 *
 * "Round the clock" with a two-hour hole at 04:00 is invisible until it is a real hole on
 * a real night, so the builder draws this and the list states it. ⚠️ BOTH READ THIS ONE
 * FUNCTION: a server-computed coverage string would be a second implementation, and the
 * day the two disagreed the list would say "Round the clock" about a pattern the builder
 * was drawing a hole in.
 *
 * ⚠️ IT IS TOTAL. A form mid-keystroke is the NORMAL case, not an error: a half-typed time
 * is dropped into `ignoredShiftIds` and never thrown. Same discipline as `slotsOf`'s
 * try/catch in `arrangementShape.ts`.
 *
 * ⚠️ GAPS AND OVERLAPS ARE WARNINGS, NEVER BLOCKERS. Nothing here returns anything a form
 * could refuse a save on. An operator may have a reason — a handover overlap, a gap they
 * cover another way — and a form that blocks with no override is how people go back to
 * keeping the real roster in a spreadsheet.
 */

export const MINUTES_IN_DAY = 1440;

export type CoverageShift = { id: string; name?: string | null; startTime: string; endTime: string };

/** A half-open interval `[startMinute, endMinute)` on the linear 0…1440 dial. */
export type CoverageInterval = { startMinute: number; endMinute: number };

export type CoverageSegment = CoverageInterval & {
  /** 0 = nobody on duty, 1 = covered, 2+ = overlap. */
  depth: number;
  /** The shifts covering it, deduped, in input order. */
  shiftIds: string[];
};

export type ShiftCoverage = {
  /** ⚠️ CONTIGUOUS, STARTING AT 0 AND ENDING AT 1440, ALWAYS. The renderer's only input. */
  segments: CoverageSegment[];
  /** LINEAR merged runs, for drawing. */
  gaps: CoverageInterval[];
  overlaps: CoverageInterval[];
  /** WRAP-FUSED, for reading and counting. `startMinute > endMinute` means it crosses midnight. */
  gapsOnDial: CoverageInterval[];
  overlapsOnDial: CoverageInterval[];
  coveredMinutes: number;
  gapMinutes: number;
  overlapMinutes: number;
  isRoundTheClock: boolean;
  /** Shifts dropped as unusable — blank or malformed. Never silently discarded. */
  ignoredShiftIds: string[];
};

export type CoverageState = 'NONE' | 'GAPS' | 'OVERLAPS' | 'ROUND_THE_CLOCK';

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `null` for anything that is not a zero-padded 24-hour `HH:mm`. */
export const parseTimeOfDay = (value: string): number | null => {
  const match = TIME.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

/**
 * 720 → `12:00 PM`; 1440 → `12:00 AM`, because a bar's right-hand edge is the end of the day.
 *
 * ⚠️ [P4-UX.2] 12-hour, like every other time an operator reads. It is built canonically
 * first and then handed to the ONE formatter — rather than doing the `% 12` here — so noon
 * and midnight are got right in one place instead of two.
 */
export const formatTimeOfDay = (minute: number): string => {
  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY, Math.round(minute)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return toTwelveHour(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
};

export const formatDuration = (minutes: number): string => {
  const whole = Math.round(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return `${String(rest)}m`;
  if (rest === 0) return `${String(hours)}h`;
  return `${String(hours)}h ${String(rest)}m`;
};

const merge = (segments: readonly CoverageSegment[], keep: (depth: number) => boolean): CoverageInterval[] => {
  const runs: CoverageInterval[] = [];
  for (const segment of segments) {
    if (!keep(segment.depth)) continue;
    const last = runs[runs.length - 1];
    if (last && last.endMinute === segment.startMinute) last.endMinute = segment.endMinute;
    else runs.push({ startMinute: segment.startMinute, endMinute: segment.endMinute });
  }
  return runs;
};

/**
 * ⚠️ IF THE FIRST RUN TOUCHES 00:00 AND THE LAST TOUCHES 24:00, THEY ARE ONE HOLE ON THE
 * DIAL. Reporting "2 gaps" for a single 23:00–01:00 hole is exactly the small lie that
 * makes an operator stop believing the bar.
 */
const fuseAcrossMidnight = (runs: readonly CoverageInterval[]): CoverageInterval[] => {
  const first = runs[0];
  const last = runs[runs.length - 1];
  if (runs.length < 2 || !first || !last || first.startMinute !== 0 || last.endMinute !== MINUTES_IN_DAY) {
    return [...runs];
  }
  return [{ startMinute: last.startMinute, endMinute: first.endMinute }, ...runs.slice(1, -1)];
};

export const coverageOf = (shifts: readonly CoverageShift[]): ShiftCoverage => {
  const ignoredShiftIds: string[] = [];
  const spans: { startMinute: number; endMinute: number; id: string }[] = [];

  for (const shift of shifts) {
    const start = parseTimeOfDay(shift.startTime);
    const end = parseTimeOfDay(shift.endTime);
    if (start === null || end === null) {
      ignoredShiftIds.push(shift.id);
      continue;
    }
    /*
     * ⚠️ `<=`, WHICH IS `slot.util.ts`'s COMPARISON EXACTLY. One rule with one meaning on
     * both sides of the app: `20:00–08:00` wraps, and `08:00–08:00` is a full 24 hours
     * rather than an empty shift.
     */
    if (end <= start) {
      spans.push({ startMinute: start, endMinute: MINUTES_IN_DAY, id: shift.id });
      spans.push({ startMinute: 0, endMinute: end, id: shift.id });
    } else {
      spans.push({ startMinute: start, endMinute: end, id: shift.id });
    }
  }

  const boundaries = [...new Set([0, MINUTES_IN_DAY, ...spans.flatMap((span) => [span.startMinute, span.endMinute])])]
    .filter((minute) => minute >= 0 && minute <= MINUTES_IN_DAY)
    .sort((left, right) => left - right);

  const segments: CoverageSegment[] = [];
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const startMinute = boundaries[index] ?? 0;
    const endMinute = boundaries[index + 1] ?? MINUTES_IN_DAY;
    if (endMinute <= startMinute) continue;

    /*
     * ⚠️ DEDUPED BY SHIFT ID, WHICH IS WHAT MAKES `08:00–08:00` READ AS DEPTH 1. Its two
     * unrolled halves are the SAME shift, and counting them twice would report a 24-hour
     * self-overlap on a pattern that is simply one long shift.
     */
    const ids = [
      ...new Set(
        spans.filter((span) => span.startMinute <= startMinute && span.endMinute >= endMinute).map((span) => span.id),
      ),
    ];
    segments.push({ startMinute, endMinute, depth: ids.length, shiftIds: ids });
  }

  const gaps = merge(segments, (depth) => depth === 0);
  const overlaps = merge(segments, (depth) => depth >= 2);
  const minutesIn = (runs: readonly CoverageInterval[]): number =>
    runs.reduce((total, run) => total + (run.endMinute - run.startMinute), 0);

  const gapMinutes = minutesIn(gaps);
  const overlapMinutes = minutesIn(overlaps);

  return {
    segments,
    gaps,
    overlaps,
    gapsOnDial: fuseAcrossMidnight(gaps),
    overlapsOnDial: fuseAcrossMidnight(overlaps),
    coveredMinutes: MINUTES_IN_DAY - gapMinutes,
    gapMinutes,
    overlapMinutes,
    isRoundTheClock: gapMinutes === 0 && MINUTES_IN_DAY - gapMinutes > 0,
    ignoredShiftIds,
  };
};

/**
 * ⚠️ A GAP OUTRANKS AN OVERLAP when a pattern has both. A gap means NOBODY is on duty; an
 * overlap means too many are. Only one of those is a hole on a real night.
 */
export const coverageState = (coverage: ShiftCoverage): CoverageState => {
  if (coverage.coveredMinutes === 0) return 'NONE';
  if (coverage.gapMinutes > 0) return 'GAPS';
  if (coverage.overlapMinutes > 0) return 'OVERLAPS';
  return 'ROUND_THE_CLOCK';
};

const listRuns = (runs: readonly CoverageInterval[]): string => {
  const named = runs
    .slice(0, 3)
    .map((run) => `${formatTimeOfDay(run.startMinute)}–${formatTimeOfDay(run.endMinute)}`)
    .join(', ');
  return runs.length > 3 ? `${named} and ${String(runs.length - 3)} more` : named;
};

/**
 * ⚠️ THE SENTENCE IS THE CONTENT, NOT A CAPTION FOR IT.
 *
 * The bar itself is decorative: a reader — and a sighted colour-blind operator, who is the
 * exact user the accessibility rule exists for — gets the state from these words. It lives
 * here rather than in the component so it is testable without rendering anything.
 */
export const describeCoverage = (coverage: ShiftCoverage): string => {
  if (coverage.coveredMinutes === 0) return 'No shifts yet, so none of the day is covered.';

  const sentences: string[] = [];
  if (coverage.gapMinutes > 0) {
    sentences.push(`${formatDuration(coverage.gapMinutes)} is not covered: ${listRuns(coverage.gapsOnDial)}.`);
  } else {
    sentences.push('All 24 hours are covered.');
  }
  if (coverage.overlapMinutes > 0) {
    sentences.push(
      `${formatDuration(coverage.overlapMinutes)} is covered twice over: ${listRuns(coverage.overlapsOnDial)}.`,
    );
  }
  return sentences.join(' ');
};
